/**
 * The fill step — PLAN §3, step 6.
 *
 * Once the mandatory essentials are committed, what's left is a 0/1 knapsack:
 * pick the subset of the remaining candidates with the most value that fits in
 * the minutes you have left. The wrinkle is prerequisites — picking an entry
 * can drag in the entries it's built on — so the items are **bundles**
 * (`{candidate} ∪ its surviving prerequisites`) rather than single entries.
 *
 * Three paths, in the order they're tried:
 *
 * 1. **Take-all.** Everything fits. Trivially optimal, and the common case for
 *    a generous budget — worth its own branch so the DP never allocates.
 * 2. **Exact 0/1 DP.** Taken whenever no two bundles share an entry, which is
 *    the normal case: ~90% of the catalog has no prerequisites at all, and an
 *    essential's prerequisites are already committed and so never appear in a
 *    bundle. n ≤ 79 items against a capacity of ~20k minutes is ~1.6M cell
 *    updates — a couple of milliseconds, no worker, no approximation.
 * 3. **Greedy by density + one swap pass.** Only when bundles overlap (two
 *    candidates sharing a prerequisite), where the DP's independence
 *    assumption would double-count the shared entry's cost and quietly return
 *    an infeasible plan. The result is reported as such — the caller turns
 *    `fallbackCause` into a user-visible warning, because a plan produced by
 *    an approximation has to say so.
 *
 * Every ordering in here is {@link compareCandidates} or
 * {@link compareByDensity}, so the same input always produces the same plan.
 */

import type { EntryId } from "@schema/catalog.ts";
import { compareByDensity, compareCandidates } from "./score.ts";

/** One entry's contribution, looked up when a bundle's members are priced. */
export interface KnapsackUnit {
  id: EntryId;
  /** Minutes. Must be a non-negative integer — the DP indexes on it. */
  cost: number;
  value: number;
  orderIndex: number;
}

/**
 * A candidate plus everything you'd have to watch first. `members` always
 * contains the candidate itself, and only entries not already committed
 * elsewhere — a prerequisite that is already a mandatory essential costs the
 * fill nothing and must not be counted twice.
 */
export interface KnapsackBundle {
  /** The candidate entry this bundle exists for. */
  id: EntryId;
  members: readonly EntryId[];
  /** Σ member cost. */
  cost: number;
  /** Σ member value. */
  value: number;
  /** The candidate's own `orderIndex` — the last tie-break. */
  orderIndex: number;
}

export type KnapsackMethod = "take-all" | "exact-dp" | "greedy-swap";

/** Why the exact path was abandoned. `null` whenever the result is optimal. */
export type FallbackCause = "overlapping-bundles" | "problem-size";

export interface KnapsackResult {
  method: KnapsackMethod;
  /** `null` means the answer is provably optimal (`take-all` and `exact-dp`). */
  fallbackCause: FallbackCause | null;
  /** Chosen bundle ids, in canonical order. */
  chosen: EntryId[];
  /** Union of the chosen bundles' members, ordered by `orderIndex`. */
  members: EntryId[];
  cost: number;
  value: number;
  /** Entries appearing in more than one bundle — what forced the fallback. */
  overlapping: EntryId[];
  /** DP cells visited; `0` on the other two paths. Kept for the perf assertion in the tests. */
  cells: number;
}

/**
 * Ceiling on DP work. 79 entries × 20k minutes ≈ 1.6M, so this leaves two
 * orders of magnitude of headroom before an absurd budget (a hundred hours a
 * week for a year) can make the UI thread stutter. Past it, greedy — and the
 * plan says so.
 */
export const MAX_DP_CELLS = 8_000_000;

export interface KnapsackInput {
  bundles: readonly KnapsackBundle[];
  /** Per-entry costs and values, for pricing bundle unions. */
  units: ReadonlyMap<EntryId, KnapsackUnit>;
  /** Minutes left after the mandatory set. Negative is treated as 0. */
  capacity: number;
  maxCells?: number;
}

function emptyResult(overlapping: EntryId[], method: KnapsackMethod): KnapsackResult {
  return {
    method,
    fallbackCause: null,
    chosen: [],
    members: [],
    cost: 0,
    value: 0,
    overlapping,
    cells: 0,
  };
}

/** Entries appearing in more than one bundle, ordered by `orderIndex`. */
function findOverlaps(
  bundles: readonly KnapsackBundle[],
  units: ReadonlyMap<EntryId, KnapsackUnit>,
): EntryId[] {
  const seen = new Map<EntryId, number>();
  for (const bundle of bundles) {
    for (const member of bundle.members) {
      seen.set(member, (seen.get(member) ?? 0) + 1);
    }
  }
  return [...seen.entries()]
    .filter(([, count]) => count > 1)
    .map(([id]) => id)
    .sort((a, b) => (units.get(a)?.orderIndex ?? 0) - (units.get(b)?.orderIndex ?? 0));
}

/** Price a set of chosen bundles by the union of their members — never double-counting. */
function priceSelection(
  chosen: ReadonlySet<EntryId>,
  byId: ReadonlyMap<EntryId, KnapsackBundle>,
  units: ReadonlyMap<EntryId, KnapsackUnit>,
): { cost: number; value: number; members: EntryId[] } {
  const members = new Set<EntryId>();
  for (const id of chosen) {
    const bundle = byId.get(id);
    if (!bundle) continue;
    for (const member of bundle.members) members.add(member);
  }
  const ordered: KnapsackUnit[] = [];
  for (const id of members) {
    const unit = units.get(id);
    if (unit) ordered.push(unit);
  }
  // Sorted before summing, not after: the value total is a float, and a float
  // sum is only reproducible across languages if the terms are added in the
  // same sequence. Set iteration order is not that guarantee.
  ordered.sort((a, b) => a.orderIndex - b.orderIndex);
  let cost = 0;
  let value = 0;
  for (const unit of ordered) {
    cost += unit.cost;
    value += unit.value;
  }
  return { cost, value, members: ordered.map((unit) => unit.id) };
}

/**
 * Exact 0/1 knapsack over independent bundles.
 *
 * Standard rolling-array DP with a keep-bit per (item, capacity) cell for
 * reconstruction. Two details that matter:
 *
 * - Alongside the best value per cell we carry the **cost** that achieved it,
 *   and prefer a cheaper subset on equal value. Without that, "maximum value"
 *   leaves the leftover minutes undefined and two runs can legitimately differ.
 * - Items are visited in {@link compareCandidates} order and a cell only
 *   changes on a strict improvement, so an exact (value, cost) tie keeps the
 *   incumbent — the subset built from the canonically earlier items.
 */
function exactDp(
  bundles: readonly KnapsackBundle[],
  capacity: number,
  byId: ReadonlyMap<EntryId, KnapsackBundle>,
  units: ReadonlyMap<EntryId, KnapsackUnit>,
): KnapsackResult {
  const items = [...bundles].sort(compareCandidates);
  const width = capacity + 1;
  const bestValue = new Float64Array(width);
  const bestCost = new Int32Array(width);
  const keep = new Uint8Array(Math.ceil((items.length * width) / 8));
  let cells = 0;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item) continue;
    const cost = item.cost;
    if (cost > capacity) continue;
    const base = i * width;
    for (let w = capacity; w >= cost; w -= 1) {
      cells += 1;
      const candidateValue = (bestValue[w - cost] ?? 0) + item.value;
      const candidateCost = (bestCost[w - cost] ?? 0) + cost;
      const incumbentValue = bestValue[w] ?? 0;
      if (
        candidateValue > incumbentValue ||
        (candidateValue === incumbentValue && candidateCost < (bestCost[w] ?? 0))
      ) {
        bestValue[w] = candidateValue;
        bestCost[w] = candidateCost;
        const bit = base + w;
        keep[bit >> 3] = (keep[bit >> 3] ?? 0) | (1 << (bit & 7));
      }
    }
  }

  const chosen = new Set<EntryId>();
  let w = capacity;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (!item) continue;
    const bit = i * width + w;
    if ((keep[bit >> 3] ?? 0) & (1 << (bit & 7))) {
      chosen.add(item.id);
      w -= item.cost;
    }
  }

  const priced = priceSelection(chosen, byId, units);
  return {
    method: "exact-dp",
    fallbackCause: null,
    chosen: [...chosen]
      .map((id) => byId.get(id))
      .filter((bundle): bundle is KnapsackBundle => bundle !== undefined)
      .sort(compareCandidates)
      .map((bundle) => bundle.id),
    members: priced.members,
    cost: priced.cost,
    value: priced.value,
    overlapping: [],
    cells,
  };
}

/**
 * Greedy by value density, then a single improvement pass of one-for-one swaps.
 *
 * Used only for overlapping bundles, where the greedy pass is what keeps the
 * arithmetic honest: bundles are priced against what's *already taken*, so a
 * shared prerequisite is paid for once. The swap pass then buys back some of
 * what greedy's first-fit ordering gives away — for each rejected candidate in
 * density order, drop the least valuable selected candidate that makes room
 * and raises the total. One pass, deterministic, O(n²) over n ≤ 79.
 */
function greedyWithSwap(
  bundles: readonly KnapsackBundle[],
  capacity: number,
  byId: ReadonlyMap<EntryId, KnapsackBundle>,
  units: ReadonlyMap<EntryId, KnapsackUnit>,
  overlapping: EntryId[],
  fallbackCause: FallbackCause,
): KnapsackResult {
  const byDensity = [...bundles].sort(compareByDensity);
  const chosen = new Set<EntryId>();
  const taken = new Set<EntryId>();
  let cost = 0;

  for (const bundle of byDensity) {
    if (taken.has(bundle.id)) continue;
    let marginal = 0;
    for (const member of bundle.members) {
      if (taken.has(member)) continue;
      marginal += units.get(member)?.cost ?? 0;
    }
    if (cost + marginal > capacity) continue;
    chosen.add(bundle.id);
    for (const member of bundle.members) taken.add(member);
    cost += marginal;
  }

  let current = priceSelection(chosen, byId, units);

  // One improvement pass. Rejected candidates in density order; the swap
  // partner is chosen by the reverse of the canonical order, so the least
  // valuable, most expensive, latest entry goes first.
  const rejected = byDensity.filter((bundle) => !chosen.has(bundle.id));
  for (const incoming of rejected) {
    if (chosen.has(incoming.id)) continue;
    const outgoing = [...chosen]
      .map((id) => byId.get(id))
      .filter((bundle): bundle is KnapsackBundle => bundle !== undefined)
      .sort((a, b) => compareCandidates(b, a));

    for (const drop of outgoing) {
      const trial = new Set(chosen);
      trial.delete(drop.id);
      trial.add(incoming.id);
      const priced = priceSelection(trial, byId, units);
      if (priced.cost <= capacity && priced.value > current.value) {
        chosen.delete(drop.id);
        chosen.add(incoming.id);
        current = priced;
        break;
      }
    }
  }

  return {
    method: "greedy-swap",
    fallbackCause,
    chosen: [...chosen]
      .map((id) => byId.get(id))
      .filter((bundle): bundle is KnapsackBundle => bundle !== undefined)
      .sort(compareCandidates)
      .map((bundle) => bundle.id),
    members: current.members,
    cost: current.cost,
    value: current.value,
    overlapping,
    cells: 0,
  };
}

/** Fill the remaining capacity. See the module header for which path runs when. */
export function solveKnapsack(input: KnapsackInput): KnapsackResult {
  const capacity = Math.max(0, Math.floor(input.capacity));
  const { units } = input;
  const bundles = input.bundles.filter((bundle) => bundle.members.length > 0);
  const byId = new Map(bundles.map((bundle) => [bundle.id, bundle] as const));
  const overlapping = findOverlaps(bundles, units);

  if (bundles.length === 0) return emptyResult(overlapping, "take-all");

  const everything = priceSelection(new Set(byId.keys()), byId, units);
  if (everything.cost <= capacity) {
    return {
      method: "take-all",
      fallbackCause: null,
      chosen: [...bundles].sort(compareCandidates).map((bundle) => bundle.id),
      members: everything.members,
      cost: everything.cost,
      value: everything.value,
      overlapping,
      cells: 0,
    };
  }

  if (overlapping.length > 0) {
    return greedyWithSwap(bundles, capacity, byId, units, overlapping, "overlapping-bundles");
  }

  const cells = bundles.length * (capacity + 1);
  if (cells > (input.maxCells ?? MAX_DP_CELLS)) {
    return greedyWithSwap(bundles, capacity, byId, units, overlapping, "problem-size");
  }

  return exactDp(bundles, capacity, byId, units);
}
